import { useEffect, useRef, useState } from "react";
import { api, thumbUrl } from "../lib/api";
import { X, Check, GalleryHorizontalEnd, Star, Flag, Info, Copy, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import RetryImage from "./RetryImage";

/**
 * Modal to pick reference images from: Mis Referencias, Externas, Biblioteca, o Carruseles.
 * When a carousel is picked, all its slides are returned with carousel role metadata
 * so the chat can build a structure-aware prompt.
 */
export default function ReferencePicker({ onClose, onPick, zClass = "z-50" }) {
    const [tab, setTab] = useState("own");
    const [items, setItems] = useState([]);
    const [carousels, setCarousels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState([]);
    const [selectedCarousels, setSelectedCarousels] = useState({}); // {group_id: true}
    const [previewItem, setPreviewItem] = useState(null); // shows analysis_json / prompt
    // Library-specific pagination. We only paginate library because it's
    // the tab that grows unbounded with usage; references stay small per
    // category. Page is 1-indexed to match the LibraryPage UX.
    const LIBRARY_PAGE_SIZE = 24;
    const [libPage, setLibPage] = useState(1);
    const [libTotal, setLibTotal] = useState(0);
    /** Inline upload from PC. Files go to /references/upload?category=own and
     * are auto-selected so the user can include them in the same generation. */
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    /** Drag & drop state for the whole picker. When the user drags files
     * over the modal we paint a periwinkle outline + drop overlay; releasing
     * triggers the same upload pipeline as the button. */
    const [dragOver, setDragOver] = useState(false);

    const handleUploadFiles = async (fileList) => {
        const files = Array.from(fileList || []).filter((f) =>
            f.type.startsWith("image/"),
        );
        if (!files.length) {
            toast.error("Solo se permiten imágenes");
            return;
        }
        setUploading(true);
        const uploaded = [];
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("category", "own");
                fd.append("title", file.name);
                const r = await api.post("/references/upload", fd);
                uploaded.push(r.data);
            }
            // Invalidate cached "own" so the next list-load fetches fresh.
            try {
                sessionStorage.removeItem("kc:picker-cache:own");
            } catch {
                /* ignore */
            }
            // Switch to "own" tab so the user sees what they just uploaded.
            setTab("own");
            // Pre-populate items + auto-select the new uploads so a single
            // "Agregar" click after upload includes them.
            setItems((prev) => [...uploaded, ...prev]);
            setSelected((prev) => [...prev, ...uploaded.map((r) => r.id)]);
            toast.success(
                files.length === 1
                    ? "Imagen subida"
                    : `${files.length} imágenes subidas`,
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Error al subir la imagen",
            );
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    // Hydrate from the session-scoped cache so repeat opens of the picker
    // feel instant. The parent Chat pre-warms this cache on mount.
    const hydrateFromCache = (t) => {
        try {
            const key = `kc:picker-cache:${t}`;
            const raw = sessionStorage.getItem(key);
            if (raw) return JSON.parse(raw);
        } catch {
            /* noop */
        }
        return null;
    };
    const saveToCache = (t, data) => {
        try {
            sessionStorage.setItem(`kc:picker-cache:${t}`, JSON.stringify(data));
        } catch {
            /* noop */
        }
    };

    useEffect(() => {
        setSelected([]);
        setSelectedCarousels({});
        // Reset library pagination when switching back to it.
        if (tab !== "library") setLibPage(1);
        // 1) Paint from cache immediately — user sees results instantly.
        //    Library is paginated so the cache key includes the page.
        const cacheKey = tab === "library" ? `library:p${libPage}` : tab;
        const cached = hydrateFromCache(cacheKey);
        if (cached) {
            if (tab === "carousels") setCarousels(cached.items || cached);
            else setItems(cached.items || cached);
            if (tab === "library" && cached.total !== undefined) {
                setLibTotal(cached.total);
            }
            setLoading(false);
        } else {
            setLoading(true);
        }
        // 2) In parallel, refresh from server — replaces the cache.
        if (tab === "carousels") {
            api.get("/references/carousels")
                .then((r) => {
                    const data = r.data || [];
                    setCarousels(data);
                    saveToCache("carousels", data);
                })
                .finally(() => setLoading(false));
        } else if (tab === "brand") {
            api.get("/brand/logos")
                .then((r) => {
                    const data = r.data || [];
                    setItems(data);
                    saveToCache("brand", data);
                })
                .finally(() => setLoading(false));
        } else if (tab === "library") {
            const skip = (libPage - 1) * LIBRARY_PAGE_SIZE;
            api.get(`/library?skip=${skip}&limit=${LIBRARY_PAGE_SIZE}`)
                .then((r) => {
                    const arr = r.data.items || [];
                    const total = r.data.total || 0;
                    setItems(arr);
                    setLibTotal(total);
                    saveToCache(cacheKey, { items: arr, total });
                })
                .finally(() => setLoading(false));
        } else {
            // Pull only the first page — the picker doesn't have a
            // "load more" UI so we cap at a healthy default (200) which
            // covers most brands. The full pagination lives in /references.
            api.get(`/references?category=${tab}&skip=0&limit=200`)
                .then((r) => {
                    const d = r.data || {};
                    const arr = Array.isArray(d) ? d : d.items || [];
                    setItems(arr);
                    saveToCache(tab, arr);
                })
                .finally(() => setLoading(false));
        }
    }, [tab, libPage]);

    /**
     * Background pre-fetch for the OTHER tabs so switching feels instant.
     *
     * We kick this off after the picker mounts. We don't block on it (no
     * setLoading), don't show errors (best-effort), and skip any tab the
     * user is currently viewing — that one loads via the main effect
     * above. Library is fetched at page 1 only (later pages load on
     * demand when the user paginates).
     *
     * Runs ONCE per picker session — there's no point pre-fetching twice
     * because the cache is sessionStorage-backed and only invalidated on
     * explicit user actions (upload, delete).
     */
    const prefetchedRef = useRef(false);
    useEffect(() => {
        if (prefetchedRef.current) return;
        prefetchedRef.current = true;
        // Give the foreground tab ~250ms to win the network race before
        // we start firing background requests. This keeps the first paint
        // fast on slow connections.
        const t = setTimeout(() => {
            const allTabs = ["own", "external", "library", "brand", "carousels"];
            for (const t2 of allTabs) {
                if (t2 === tab) continue;
                if (hydrateFromCache(t2 === "library" ? "library:p1" : t2)) continue;
                const url =
                    t2 === "carousels"
                        ? "/references/carousels"
                        : t2 === "brand"
                          ? "/brand/logos"
                          : t2 === "library"
                            ? `/library?skip=0&limit=${LIBRARY_PAGE_SIZE}`
                            : `/references?category=${t2}&skip=0&limit=200`;
                api.get(url)
                    .then((r) => {
                        if (t2 === "library") {
                            saveToCache("library:p1", {
                                items: r.data.items || [],
                                total: r.data.total || 0,
                            });
                        } else if (
                            t2 !== "carousels" && t2 !== "brand"
                        ) {
                            // /references now returns {items,total,...}
                            const d = r.data || {};
                            saveToCache(t2, Array.isArray(d) ? d : d.items || []);
                        } else {
                            saveToCache(t2, r.data || []);
                        }
                    })
                    .catch(() => {
                        /* best-effort, no UI surface */
                    });
            }
        }, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggle = (it) => {
        const key = it.file_id;
        setSelected((s) =>
            s.find((x) => x.file_id === key)
                ? s.filter((x) => x.file_id !== key)
                : [
                      ...s,
                      {
                          file_id: key,
                          storage_path: it.storage_path,
                          title: it.title || "ref",
                      },
                  ],
        );
    };

    const toggleCarousel = (c) => {
        setSelectedCarousels((prev) => ({
            ...prev,
            [c.id]: !prev[c.id],
        }));
    };

    const confirm = () => {
        if (tab === "carousels") {
            // Flatten all selected carousels' slides, preserve role + position.
            const picked = carousels
                .filter((c) => selectedCarousels[c.id])
                .flatMap((c) =>
                    (c.slides || [])
                        .slice()
                        .sort(
                            (a, b) =>
                                (a.carousel_position || 0) - (b.carousel_position || 0),
                        )
                        .map((s) => ({
                            file_id: s.file_id,
                            storage_path: s.storage_path,
                            title: `${c.title} · ${s.carousel_role}`,
                            carousel_group_id: c.id,
                            carousel_group_title: c.title,
                            carousel_role: s.carousel_role,
                            carousel_position: s.carousel_position,
                            carousel_total: c.slides?.length || 0,
                        })),
                );
            onPick(picked);
        } else {
            onPick(selected);
        }
    };

    const totalSelected =
        tab === "carousels"
            ? Object.values(selectedCarousels).filter(Boolean).length
            : selected.length;

    const ROLE_STYLES = {
        cover: { label: "Portada", color: "#a5a6ff", icon: Star },
        closing: { label: "Cierre", color: "#ffbe7b", icon: Flag },
        body: { label: "Body", color: "#8fe7d0", icon: GalleryHorizontalEnd },
    };

    return (
        <div
            data-testid="reference-picker"
            className={`fixed inset-0 ${zClass} bg-ink-950/80 backdrop-blur-sm flex items-center justify-center p-6`}
            onClick={onClose}
        >
            <div
                className="w-full max-w-4xl max-h-[80vh] rounded-2xl border hairline border-ink-600 bg-ink-900 flex flex-col overflow-hidden relative"
                onClick={(e) => e.stopPropagation()}
                onDragEnter={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer?.types?.includes("Files")) setDragOver(true);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer?.types?.includes("Files")) setDragOver(true);
                }}
                onDragLeave={(e) => {
                    // Only clear when leaving the picker entirely, not its children.
                    if (e.currentTarget.contains(e.relatedTarget)) return;
                    setDragOver(false);
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    if (e.dataTransfer?.files?.length) {
                        handleUploadFiles(e.dataTransfer.files);
                    }
                }}
            >
                {dragOver && (
                    <div
                        className="absolute inset-2 z-40 rounded-xl border-2 border-dashed border-periwinkle-500 bg-periwinkle-500/10 flex items-center justify-center pointer-events-none"
                        data-testid="picker-drop-overlay"
                    >
                        <div className="text-center">
                            <Upload className="w-10 h-10 text-periwinkle-500 mx-auto mb-2" />
                            <p className="font-display text-xl text-fog-100">
                                Suelta para subir
                            </p>
                            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mt-1">
                                Las imágenes irán a tus referencias
                            </p>
                        </div>
                    </div>
                )}
                <header className="px-6 py-4 border-b hairline border-ink-600 flex items-center justify-between gap-4">
                    <div>
                        <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase">
                            Agregar referencia
                        </p>
                        <h3 className="font-display text-2xl text-fog-100 mt-0.5">
                            Elige tus imágenes base
                        </h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            data-testid="picker-upload-input"
                            onChange={(e) => handleUploadFiles(e.target.files)}
                        />
                        <button
                            data-testid="picker-upload-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="rounded-xl border hairline border-periwinkle-500 text-periwinkle-500 hover:bg-periwinkle-500 hover:text-ink-950 px-4 py-2 text-xs font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
                            title="Sube una imagen desde tu computador para usarla como referencia"
                        >
                            {uploading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Upload className="w-4 h-4" />
                            )}
                            <span className="font-mono tracking-widest uppercase text-[10px]">
                                {uploading ? "Subiendo…" : "Desde PC"}
                            </span>
                        </button>
                        <button
                            data-testid="picker-close-btn"
                            onClick={onClose}
                            className="w-8 h-8 rounded-lg text-fog-400 hover:text-periwinkle-500"
                        >
                            <X />
                        </button>
                    </div>
                </header>
                <div className="px-6 pt-4 flex gap-2 flex-wrap">
                    <Tab active={tab === "brand"} onClick={() => setTab("brand")} label="Marca" />
                    <Tab active={tab === "own"} onClick={() => setTab("own")} label="Mis Referencias" />
                    <Tab active={tab === "external"} onClick={() => setTab("external")} label="Externas" />
                    <Tab active={tab === "library"} onClick={() => setTab("library")} label="Biblioteca" />
                    <Tab active={tab === "carousels"} onClick={() => setTab("carousels")} label="Carruseles" />
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {loading && <p className="text-fog-400 text-sm">Cargando…</p>}

                    {tab !== "carousels" && !loading && items.length === 0 && (
                        <p className="text-fog-400 text-sm">
                            {tab === "brand"
                                ? "Esta marca aún no tiene logos cargados. Súbelos en /referencias → Marca."
                                : "No hay imágenes aquí todavía."}
                        </p>
                    )}
                    {tab !== "carousels" && (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2.5">
                            {items.map((it) => {
                                const sel = selected.find((x) => x.file_id === it.file_id);
                                const isLogo = tab === "brand";
                                // A reference is "analysed" when the vision
                                // pipeline has populated analysis_json.prompt
                                // — that's what we surface to the user.
                                const hasPrompt = !!(
                                    it.analysis_json?.prompt ||
                                    it.analysis_json?.suggested_prompt ||
                                    it.title
                                );
                                return (
                                    <button
                                        key={it.id}
                                        type="button"
                                        data-testid={`picker-item-${it.id}`}
                                        onClick={() => toggle(it)}
                                        className={`group aspect-square rounded-xl overflow-hidden border-2 transition-all relative ${
                                            sel
                                                ? "border-periwinkle-500"
                                                : "border-ink-600 hover:border-fog-500"
                                        } ${isLogo ? "bg-white" : ""}`}
                                    >
                                        <RetryImage
                                            src={thumbUrl(it.storage_path, 240)}
                                            alt={it.title || it.label || "ref"}
                                            loading="lazy"
                                            decoding="async"
                                            className={`w-full h-full ${
                                                isLogo ? "object-contain p-2" : "object-cover"
                                            }`}
                                        />
                                        {isLogo && (it.label || it.title) && (
                                            <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] font-mono px-1.5 py-0.5 truncate">
                                                {it.label || it.title}
                                            </span>
                                        )}
                                        {hasPrompt && !isLogo && (
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                data-testid={`picker-item-info-${it.id}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPreviewItem(it);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.stopPropagation();
                                                        setPreviewItem(it);
                                                    }
                                                }}
                                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/55 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-periwinkle-500 transition"
                                                title="Ver prompt"
                                            >
                                                <Info className="w-3 h-3" />
                                            </span>
                                        )}
                                        {sel && (
                                            <span className="absolute inset-0 bg-periwinkle-500/30 flex items-center justify-center">
                                                <span className="w-7 h-7 rounded-full bg-periwinkle-500 text-ink-950 flex items-center justify-center">
                                                    <Check className="w-4 h-4" strokeWidth={3} />
                                                </span>
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {tab === "library" && !loading && libTotal > LIBRARY_PAGE_SIZE && (
                        <div
                            data-testid="picker-library-pagination"
                            className="mt-4 flex items-center justify-center gap-3"
                        >
                            <button
                                type="button"
                                data-testid="picker-library-prev"
                                disabled={libPage <= 1 || loading}
                                onClick={() => setLibPage((p) => Math.max(1, p - 1))}
                                className="rounded-lg border hairline border-ink-600 text-fog-300 hover:text-periwinkle-500 hover:border-periwinkle-500 px-3 py-1.5 text-xs disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                                ← Anterior
                            </button>
                            <span className="font-mono text-[10px] tracking-widest text-fog-400 uppercase tabular-nums">
                                Página {libPage} de {Math.max(1, Math.ceil(libTotal / LIBRARY_PAGE_SIZE))}
                            </span>
                            <button
                                type="button"
                                data-testid="picker-library-next"
                                disabled={libPage * LIBRARY_PAGE_SIZE >= libTotal || loading}
                                onClick={() => setLibPage((p) => p + 1)}
                                className="rounded-lg border hairline border-ink-600 text-fog-300 hover:text-periwinkle-500 hover:border-periwinkle-500 px-3 py-1.5 text-xs disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                                Siguiente →
                            </button>
                        </div>
                    )}

                    {tab === "carousels" && !loading && carousels.length === 0 && (
                        <div
                            data-testid="picker-carousels-empty"
                            className="text-center py-10"
                        >
                            <GalleryHorizontalEnd className="w-8 h-8 text-fog-500 mx-auto mb-2" />
                            <p className="text-fog-400 text-sm">
                                Aún no hay carruseles subidos.
                            </p>
                        </div>
                    )}
                    {tab === "carousels" && (
                        <div className="space-y-3">
                            {carousels.map((c) => {
                                const sel = !!selectedCarousels[c.id];
                                return (
                                    <button
                                        type="button"
                                        key={c.id}
                                        data-testid={`picker-carousel-${c.id}`}
                                        onClick={() => toggleCarousel(c)}
                                        className={`w-full text-left rounded-xl border hairline p-3 flex items-center gap-3 transition-colors ${
                                            sel
                                                ? "border-periwinkle-500 bg-periwinkle-500/5"
                                                : "border-ink-600 hover:border-ink-500"
                                        }`}
                                    >
                                        <div className="flex -space-x-2 shrink-0">
                                            {(c.slides || []).slice(0, 4).map((s) => (
                                                <div
                                                    key={s.id}
                                                    className="w-12 h-12 rounded-lg overflow-hidden border-2 border-ink-900"
                                                >
                                                    <RetryImage
                                                        src={thumbUrl(s.storage_path, 160)}
                                                        alt={s.title}
                                                        loading="lazy"
                                                        decoding="async"
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <GalleryHorizontalEnd className="w-3.5 h-3.5 text-periwinkle-500" />
                                                <p className="text-fog-100 text-sm font-medium truncate">
                                                    {c.title}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap mt-1">
                                                {(c.slides || []).map((s) => {
                                                    const m = ROLE_STYLES[s.carousel_role] || ROLE_STYLES.body;
                                                    return (
                                                        <span
                                                            key={s.id}
                                                            className="font-mono text-[9px] tracking-widest uppercase"
                                                            style={{ color: m.color }}
                                                        >
                                                            {m.label.charAt(0)}
                                                        </span>
                                                    );
                                                })}
                                                <span className="font-mono text-[10px] text-fog-500 ml-2">
                                                    · {c.slides?.length || 0} slides
                                                </span>
                                            </div>
                                        </div>
                                        {sel && (
                                            <span className="w-8 h-8 rounded-full bg-periwinkle-500 text-ink-950 flex items-center justify-center shrink-0">
                                                <Check className="w-4 h-4" strokeWidth={3} />
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <footer className="px-6 py-4 border-t hairline border-ink-600 flex items-center justify-between">
                    <p className="font-mono text-xs text-fog-400">
                        {totalSelected}{" "}
                        {tab === "carousels"
                            ? `carrusel${totalSelected === 1 ? "" : "es"}`
                            : `seleccionada${totalSelected === 1 ? "" : "s"}`}
                    </p>
                    <button
                        data-testid="picker-confirm-btn"
                        disabled={totalSelected === 0}
                        onClick={confirm}
                        className="px-5 py-2 rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Agregar
                    </button>
                </footer>
            </div>
            {previewItem && (
                <PromptPreviewModal
                    item={previewItem}
                    onClose={() => setPreviewItem(null)}
                />
            )}
        </div>
    );
}

/**
 * Lightweight modal that surfaces the analysis prompt of a reference so
 * the user can peek at its semantic description and copy it to reuse as
 * a seed for their next generation prompt. Renders on top of the picker.
 */
function PromptPreviewModal({ item, onClose }) {
    const prompt =
        item.analysis_json?.prompt ||
        item.analysis_json?.suggested_prompt ||
        item.title ||
        "";
    const tags = Array.isArray(item.analysis_json?.tags)
        ? item.analysis_json.tags.slice(0, 12)
        : [];
    const copy = () => {
        navigator.clipboard.writeText(prompt);
        toast.success("Prompt copiado");
    };
    return (
        <div
            data-testid="picker-prompt-preview"
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-ink-950 border hairline border-periwinkle-500/30 rounded-2xl w-full max-w-2xl overflow-hidden"
            >
                <div className="flex items-center justify-between px-6 py-4 border-b hairline border-ink-600">
                    <p className="font-mono text-[10px] tracking-[0.25em] text-periwinkle-500 uppercase">
                        Prompt · descripción semántica
                    </p>
                    <button
                        onClick={onClose}
                        className="text-fog-400 hover:text-fog-100"
                        aria-label="Cerrar"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="grid md:grid-cols-[220px_1fr] gap-4 p-6">
                    <div className="rounded-xl overflow-hidden bg-ink-900 border hairline border-ink-600 aspect-square">
                        <RetryImage
                            src={thumbUrl(item.storage_path, 360)}
                            alt={item.title || "ref"}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="min-w-0 space-y-3">
                        {prompt ? (
                            <div className="relative">
                                <p className="text-fog-100 text-sm leading-relaxed max-h-64 overflow-y-auto pr-10 whitespace-pre-wrap">
                                    {prompt}
                                </p>
                                <button
                                    data-testid="picker-prompt-copy"
                                    onClick={copy}
                                    className="absolute top-0 right-0 rounded-full bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 w-8 h-8 flex items-center justify-center"
                                    title="Copiar"
                                >
                                    <Copy className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ) : (
                            <p className="text-fog-400 text-sm">
                                Aún no hay análisis para esta imagen. El proceso corre en segundo plano.
                            </p>
                        )}
                        {tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {tags.map((t) => (
                                    <span
                                        key={t}
                                        className="px-2 py-0.5 rounded-full border hairline border-ink-600 text-fog-400 font-mono text-[10px]"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Tab({ active, label, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`picker-tab-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={`px-4 py-2 rounded-lg text-xs font-mono tracking-wider uppercase transition-colors ${
                active
                    ? "bg-ink-800 text-periwinkle-500 border hairline border-periwinkle-500"
                    : "text-fog-400 hover:text-fog-100"
            }`}
        >
            {label}
        </button>
    );
}
